export interface IntroAudioOptions {
  src: string;
}

type Listener = () => void;

export class IntroAudio {
  private audio: HTMLAudioElement | null = null;
  private src: string;
  private canPlayListeners = new Set<Listener>();
  private endedListeners = new Set<Listener>();

  constructor(opts: IntroAudioOptions) {
    this.src = opts.src;
  }

  preload(): void {
    if (this.audio) return;
    const audio = new Audio(this.src);
    audio.preload = 'auto';
    audio.load();
    audio.addEventListener('canplaythrough', this.handleCanPlay);
    audio.addEventListener('ended', this.handleEnded);
    this.audio = audio;
  }

  async play(): Promise<void> {
    if (!this.audio) {
      this.preload();
    }
    return this.audio!.play();
  }

  setMuted(muted: boolean): void {
    if (this.audio) {
      this.audio.muted = muted;
    }
  }

  onCanPlay(cb: Listener): () => void {
    this.canPlayListeners.add(cb);
    return () => {
      this.canPlayListeners.delete(cb);
    };
  }

  onEnded(cb: Listener): () => void {
    this.endedListeners.add(cb);
    return () => {
      this.endedListeners.delete(cb);
    };
  }

  destroy(): void {
    if (!this.audio) return;
    this.audio.removeEventListener('canplaythrough', this.handleCanPlay);
    this.audio.removeEventListener('ended', this.handleEnded);
    try {
      this.audio.pause();
    } catch {
      /* ignore */
    }
    this.audio = null;
    this.canPlayListeners.clear();
    this.endedListeners.clear();
  }

  private handleCanPlay = (): void => {
    this.canPlayListeners.forEach((cb) => cb());
  };

  private handleEnded = (): void => {
    this.endedListeners.forEach((cb) => cb());
  };
}
